"use client";

import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IoEyeSharp, IoEyeOffSharp } from "react-icons/io5";
import { FaCalendarAlt, FaEnvelope, FaHistory } from "react-icons/fa";
import { AppDispatch } from "../../../feature/store/store";
import { setUserId, setUserInfo, userLoginApi } from "../../../feature/reducers/userSlice";
import { NotificationService } from "../../../service/NotificationService";

// Beim Login nur prüfen, ob etwas eingegeben wurde – die Passwort-Regeln gelten
// beim Registrieren. Sonst könnten sich User mit älteren Passwörtern nicht mehr einloggen.
const formSchema = Yup.object({
  email: Yup.string().email("Ungültige E-Mail-Adresse").required("E-Mail ist erforderlich"),
  password: Yup.string().required("Passwort ist erforderlich"),
});

const inputClass =
  "h-[52px] w-full rounded-[10px] border bg-ao-bg px-4 text-[15px] text-ao-fg placeholder:text-ao-muted transition-colors focus:border-ao-accent focus:outline-none";

const BENEFITS = [
  { icon: <FaCalendarAlt />, text: "Werkstatttermine online buchen" },
  { icon: <FaHistory />, text: "Alle Ihre Termine auf einen Blick" },
  { icon: <FaEnvelope />, text: "Bestätigung direkt per E-Mail" },
];

const Login = () => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: formSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const response = await dispatch(userLoginApi(values)).unwrap();
        // userLoginApi liefert bei falschem Passwort einen Text statt eines Fehlers
        if (!response?.userInfo) {
          throw new Error(typeof response === "string" ? response : "Login fehlgeschlagen.");
        }
        dispatch(setUserInfo(response.userInfo));
        dispatch(setUserId(response.userInfo.userId));
        NotificationService.success("Willkommen zurück!");
        router.push("/");
      } catch (error: any) {
        NotificationService.error(
          error?.message === "Invalid email or password" || error?.message === "User not found"
            ? "E-Mail oder Passwort ist falsch."
            : error?.message || "Login fehlgeschlagen."
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  const emailError = formik.touched.email && formik.errors.email;
  const passwordError = formik.touched.password && formik.errors.password;

  return (
    <section className="bg-ao-bg text-ao-fg">
      <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-[1440px] lg:grid-cols-2">

        {/* Links (nur Laptop): Vorteile */}
        <div className="hidden flex-col justify-center gap-10 px-20 py-16 lg:flex">
          <div className="flex flex-col gap-5">
            <span className="text-[13px] font-semibold tracking-[0.18em] text-ao-accent-fg">MEIN KONTO</span>
            <h1 className="font-display text-7xl font-extrabold uppercase leading-[0.92]">
              Willkommen
              <br />
              <span className="text-ao-accent-fg">zurück.</span>
            </h1>
            <p className="max-w-md text-lg leading-relaxed text-ao-muted">
              Melden Sie sich an und behalten Sie Ihre Werkstatttermine im Blick.
            </p>
          </div>
          <ul className="flex flex-col gap-4">
            {BENEFITS.map((b) => (
              <li key={b.text} className="flex items-center gap-4 text-[17px] font-semibold">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ao-surface-2 text-ao-accent-fg">
                  {b.icon}
                </span>
                {b.text}
              </li>
            ))}
          </ul>
        </div>

        {/* Rechts: Formular */}
        <div className="flex items-center justify-center px-5 py-12 lg:px-20">
          <div className="flex w-full max-w-md flex-col gap-8">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-5xl font-extrabold uppercase leading-none">Anmelden</h2>
              <p className="text-[15px] text-ao-muted">
                Noch kein Konto?{" "}
                <Link href="/register" className="font-bold text-ao-accent-fg hover:underline">
                  Jetzt registrieren
                </Link>
              </p>
            </div>

            <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-[13px] font-semibold text-ao-muted">
                  E-Mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="ihre@email.de"
                  aria-invalid={!!emailError}
                  className={`${inputClass} ${emailError ? "border-red-500" : "border-ao-line-strong"}`}
                />
                {emailError && <p className="text-sm text-red-600 dark:text-red-400">{formik.errors.email}</p>}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-[13px] font-semibold text-ao-muted">
                    Passwort
                  </label>
                  <Link href="/forgetPassword" className="text-[13px] font-semibold text-ao-accent-fg hover:underline">
                    Passwort vergessen?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Ihr Passwort"
                    aria-invalid={!!passwordError}
                    className={`${inputClass} pr-12 ${passwordError ? "border-red-500" : "border-ao-line-strong"}`}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-ao-muted hover:text-ao-fg"
                  >
                    {showPassword ? <IoEyeOffSharp className="text-lg" /> : <IoEyeSharp className="text-lg" />}
                  </button>
                </div>
                {passwordError && <p className="text-sm text-red-600 dark:text-red-400">{formik.errors.password}</p>}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-2 flex h-[54px] items-center justify-center rounded-xl bg-ao-accent text-base font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoading ? "Wird angemeldet…" : "Anmelden"}
              </button>
            </form>

            <Link href="/" className="text-center text-sm font-semibold text-ao-muted hover:text-ao-fg">
              ← Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
